import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  X,
  Info,
  UserMinus,
  Calculator,
  Search,
  Save,
  Trash2,
} from 'lucide-react';
import '../CSS/GuideModal.css';

const WithdrawnGuideModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setIsOpen(false);
  };

  return (
    <>
      {/* ── Floating Action Button ── */}
      <div className="gml-fab-wrapper">
        <span className="gml-fab-tooltip">Need help?</span>
        <button
          className="gml-fab"
          onClick={() => setIsOpen(true)}
          aria-label="Open Withdrawn Students Guide"
          title="Withdrawn Students Guide"
        >
          <BookOpen className="gml-fab-icon" />
        </button>
      </div>

      {/* ── Modal ── */}
      {isOpen && (
        <div
          className="gml-backdrop"
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="gml-modal-title"
        >
          <div className="gml-modal">

            {/* Header */}
            <div className="gml-modal-header">
              <div className="gml-modal-title-wrap">
                <BookOpen className="gml-modal-header-icon" />
                <h2 className="gml-modal-title" id="gml-modal-title">
                  Withdrawn Students Guide
                </h2>
              </div>
              <button
                className="gml-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close guide"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="gml-modal-body">

              {/* Welcome Banner */}
              <div className="gml-welcome-banner">
                <Info className="gml-welcome-icon" />
                <div>
                  <p className="gml-welcome-heading">
                    Welcome to the Withdrawn Students Module!
                  </p>
                  <p className="gml-welcome-text">
                    This guide will help you navigate the Withdrawn Students page —
                    viewing withdrawn students and calculating, saving, or deleting
                    their refund calculations. Use it anytime you need a quick
                    reference.
                  </p>
                </div>
              </div>

              {/* ── Key Features Overview ── */}
              <h3 className="gml-section-heading">Key Features Overview</h3>
              <div className="gml-cards-grid">

                {/* Withdrawn Students List */}
                <div className="gml-feature-card gml-feature-card--blue">
                  <div className="gml-card-head gml-card-head--blue">
                    <UserMinus className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Withdrawn Students List</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">1</span>
                        View students who have withdrawn only
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">2</span>
                        Filter by student name and class
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">3</span>
                        See registration date, withdrawal date, and School ID
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">4</span>
                        View total fee, amount paid, and outstanding balance
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Refund Calculator */}
                <div className="gml-feature-card gml-feature-card--green">
                  <div className="gml-card-head gml-card-head--green">
                    <Calculator className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Refund Calculator</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">1</span>
                        Calculate a refund for any withdrawn student
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">2</span>
                        Enter modules/paces, damage cost, and canteen credits
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">3</span>
                        Save the completed calculation for later reference
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">4</span>
                        Delete a saved calculation if it needs to be redone
                      </li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Detailed Feature Guide ── */}
              <h3 className="gml-section-heading">Detailed Feature Guide</h3>
              <div className="gml-detail-section">

                {/* Viewing & Filtering Withdrawn Students */}
                <div className="gml-detail-card gml-detail-card--cyan">
                  <div className="gml-detail-head gml-detail-head--cyan">
                    <Search className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Viewing &amp; Filtering Withdrawn Students</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--cyan">What Shows Here</p>
                    <ul className="gml-detail-list">
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--success">Withdrawn only</span>
                        — expelled and passed-out students are never listed on this page
                      </li>
                      <li>Each row shows School ID, student name, class, registration date, and withdrawal date</li>
                      <li>Total school fee, amount paid, and outstanding balance are shown for quick reference</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Search and Filter</p>
                    <ul className="gml-detail-list">
                      <li>Use the search box to find a student by name</li>
                      <li>Use the class dropdown to narrow the list to one grade</li>
                      <li>The summary bar above the table shows totals for the students currently loaded</li>
                    </ul>
                  </div>
                </div>

                {/* Using the Refund Calculator */}
                <div className="gml-detail-card gml-detail-card--yellow">
                  <div className="gml-detail-head gml-detail-head--yellow">
                    <Calculator className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Using the Refund Calculator</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--yellow">Opening the Calculator</p>
                    <ul className="gml-detail-list">
                      <li>Click the calculator icon on a student's row to expand their refund panel</li>
                      <li>Enrolled days are counted automatically from registration date to withdrawal date</li>
                      <li>The daily rate is applied automatically — K10/day for Grade 11 &amp; 12, K8.21/day for Grade 6–10</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">What You Enter</p>
                    <ul className="gml-detail-list">
                      <li><strong>Modules/Paces Taken</strong> — number of modules or paces completed, charged at K20 each</li>
                      <li><strong>Damage Cost</strong> — any cost of damaged school property to deduct</li>
                      <li><strong>Canteen Credits</strong> — any outstanding canteen credit to deduct</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Reading the Result</p>
                    <ul className="gml-detail-list">
                      <li>Refund Amount = Paid School Fee − (Daily Cost + K200 Registration Fee + Modules Cost + Damage Cost + Canteen Credits)</li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--muted">Not Eligible</span>
                        — shown as K0.00 whenever the calculated refund is zero or negative
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Saving & Deleting Calculations */}
                <div className="gml-detail-card gml-detail-card--green">
                  <div className="gml-detail-head gml-detail-head--green">
                    <Save className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Saving &amp; Deleting Calculations</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--green">Saving a Calculation</p>
                    <ul className="gml-detail-list">
                      <li>Click <strong>Save Refund Calculation</strong> to store the full breakdown for that student</li>
                      <li>A green dot on the calculator icon shows a calculation has already been saved</li>
                      <li>The "Last saved" date shows when it was last recorded</li>
                      <li>Saving again for the same student updates the figures already on file</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Deleting a Calculation</p>
                    <ul className="gml-detail-list">
                      <li>Click <Trash2 size={13} style={{ display: 'inline', verticalAlign: '-2px' }} /> <strong>Delete Saved Calculation</strong> to remove it</li>
                      <li>A confirmation window appears before anything is deleted</li>
                      <li>Deleting clears all saved history for that student and resets the input fields</li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Best Practices ── */}
              <div className="gml-best-practices">
                <p className="gml-best-practices-heading">Tips for Effective Use</p>
                <ul className="gml-best-practices-list">
                  {[
                    'Double-check the withdrawal date shown before calculating a refund — it comes from the Attendance module.',
                    'Confirm the daily rate matches the student\'s grade before saving a calculation.',
                    'Update and re-save a calculation if damage cost or canteen credits change later.',
                    'Cross-check outstanding and paid amounts here against the School Fee module if anything looks off.',
                    'Delete a saved calculation before recalculating if you need to start fresh.',
                    'Access this guide anytime via the floating button on the bottom-right of the screen.',
                  ].map((tip, i) => (
                    <li key={i}>
                      <span className="gml-bp-dot" aria-hidden="true" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* Footer */}
            <div className="gml-modal-footer">
              <button className="gml-close-btn" onClick={() => setIsOpen(false)}>
                Got it
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default WithdrawnGuideModal;